import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu } from "@headlessui/react";
import { MoreHorizontal, Plus, Search } from "lucide-react";
import toast from "react-hot-toast";
import { useOurScoutRequests } from "../../hooks/scout-request/useOurScoutRequests";
import { CategoryPill, StatusPill } from "../../components/scout-request/ScoutPills";
import ScoutRequestFormModal from "../../components/scout-request/ScoutRequestFormModal";
import CloseScoutRequestDialog from "../../components/scout-request/CloseScoutRequestDialog";
import { formatPositionAge, formatScoutDateAbsolute, formatScoutHorizon } from "../../utils/scoutFormat";

const SEGMENTS = [
  { value: "", label: "All" },
  { value: "professional", label: "Professional" },
  { value: "youth", label: "Youth" },
];

const CardSkeleton = () => (
  <div className="bg-white rounded-xl p-5 border border-[#E5E7EB] animate-pulse">
    <div className="flex justify-between mb-4">
      <div className="h-5 w-40 bg-gray-100 rounded-full" />
      <div className="h-5 w-28 bg-gray-100 rounded" />
    </div>
    <div className="h-4 w-56 bg-gray-100 rounded mb-2" />
    <div className="h-3 w-72 bg-gray-100 rounded mb-4" />
    <div className="h-9 w-40 bg-gray-100 rounded-lg ml-auto" />
  </div>
);

// Recommendation-count line (design 1b) — CLUB-ONLY. "7 recommendations · 2 new".
const CountLine = ({ counts, closed }) => {
  if (!counts) return null;
  const total = counts.total || 0;
  const label = `${total} recommendation${total === 1 ? "" : "s"}`;
  return (
    <span className={`text-[12.5px] font-semibold ${closed ? "text-[#9CA3AF]" : "text-[#111827]"}`}>
      {label}
      {counts.new > 0 && <span className="text-[#1B6FE0]"> · {counts.new} new</span>}
    </span>
  );
};

const ClubScoutRequestCard = ({ request, onView, onEdit, onCopyLink, onClose }) => {
  const closed = request.status === "closed";
  return (
    <div className="bg-white rounded-xl p-5 border border-[#E5E7EB] shadow-[0_1px_2px_rgba(17,24,39,0.04)]">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <CategoryPill category={request.category} />
          <StatusPill status={request.status} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <CountLine counts={request.counts} closed={closed} />
          <Menu as="div" className="relative">
            <Menu.Button className="p-1 text-[#9CA3AF] hover:text-[#111827] rounded" aria-label="More">
              <MoreHorizontal className="w-5 h-5" />
            </Menu.Button>
            <Menu.Items className="absolute right-0 mt-1 w-52 bg-white border border-[#E5E7EB] rounded-[10px] shadow-[0_8px_24px_rgba(17,24,39,0.12)] py-1 z-20 focus:outline-none">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => onEdit(request)}
                    className={`w-full text-left px-4 py-2 text-[13px] ${active ? "bg-gray-50" : ""}`}
                  >
                    Edit request
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => onCopyLink(request)}
                    className={`w-full text-left px-4 py-2 text-[13px] ${active ? "bg-gray-50" : ""}`}
                  >
                    Copy public link
                  </button>
                )}
              </Menu.Item>
              {!closed && (
                <>
                  <div className="my-1 border-t border-[#E5E7EB]" />
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => onClose(request)}
                        className={`w-full text-left px-4 py-2 text-[13px] text-[#DC2626] ${active ? "bg-[#FEF2F2]" : ""}`}
                      >
                        Close request
                      </button>
                    )}
                  </Menu.Item>
                </>
              )}
            </Menu.Items>
          </Menu>
        </div>
      </div>

      <p className={`text-[15px] font-semibold ${closed ? "text-[#9CA3AF]" : "text-[#111827]"}`}>
        {formatPositionAge(request)}
      </p>
      <p className="text-[12.5px] text-[#6B7280] mt-1">
        Horizon: {formatScoutHorizon(request.horizon)}
        {request.level_expectation ? ` · ${request.level_expectation}` : ""}
      </p>
      {request.context_line && (
        <p className="text-[12.5px] italic text-[#374151] mt-2">&ldquo;{request.context_line}&rdquo;</p>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F3F4F6]">
        <span className="text-[11.5px] text-[#9CA3AF]">
          Posted by {request.created_by?.name || "Member"} · {formatScoutDateAbsolute(request.created_at)}
          {closed && request.closed_at ? ` · closed ${formatScoutDateAbsolute(request.closed_at)}` : ""}
        </span>
        <button
          onClick={() => onView(request)}
          className="px-3.5 py-2 rounded-lg bg-[#2283FF] text-white text-[12.5px] font-semibold hover:bg-[#1B6FE0]"
        >
          View recommendations
        </button>
      </div>
    </div>
  );
};

const OurScoutRequests = () => {
  const navigate = useNavigate();
  const {
    requests,
    loading,
    error,
    category,
    changeCategory,
    fetchRequests,
    closeRequest,
  } = useOurScoutRequests();

  const [formState, setFormState] = useState(null); // {mode, initial} | null
  const [closing, setClosing] = useState(null); // request | null
  const [closeBusy, setCloseBusy] = useState(false);

  const onCopyLink = (request) => {
    const url = `${window.location.origin}/scout-requests/${request.id}`;
    navigator.clipboard?.writeText(url);
    toast.success("Public link copied.");
  };

  // DECISION-002: on keep the dialog hands over the chosen radar list (null = none
  // picked → the server keeps them in the default My Radar).
  const confirmClose = async (keepLongTerm, targetListId) => {
    setCloseBusy(true);
    const res = await closeRequest(closing.id, keepLongTerm, targetListId);
    setCloseBusy(false);
    if (res.success) {
      toast.success("Scout request closed.");
      setClosing(null);
    } else {
      toast.error(res.error || "Could not close the request.");
    }
  };

  return (
    <div className="min-h-screen bg-[#EDF0F4]">
      {/* Platform-standard container (W1-04): no fixed max-width, page
          scales to 2560+ via the progressive padding ladder. */}
      <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 py-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">Scout requests</h1>
            <p className="text-[13px] text-[#6B7280] mt-1">
              Public under your name · recommendations visible only to you
            </p>
          </div>
          <button
            onClick={() => setFormState({ mode: "create", initial: null })}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0] flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            New scout request
          </button>
        </div>

        {/* Segmented filter */}
        <div className="inline-flex rounded-lg bg-white border border-[#E5E7EB] p-0.5 mb-5">
          {SEGMENTS.map((seg) => {
            const active = category === seg.value;
            return (
              <button
                key={seg.value || "all"}
                onClick={() => changeCategory(seg.value)}
                aria-pressed={active}
                className={`px-4 py-1.5 rounded-md text-[13px] font-semibold transition ${
                  active ? "bg-[#E8F1FF] text-[#1B6FE0]" : "text-[#6B7280] hover:text-[#111827]"
                }`}
              >
                {seg.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-20 text-center">
            <p className="text-red-500 mb-3">{error}</p>
            <button onClick={() => fetchRequests(1)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Try again
            </button>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
            <div className="bg-white border border-[#E5E7EB] rounded-full p-4 mb-4">
              <Search className="w-7 h-7 text-[#9CA3AF]" />
            </div>
            <h3 className="text-[17px] font-bold text-[#111827] mb-1.5">No scout requests yet</h3>
            <p className="text-[13px] text-[#6B7280] max-w-sm mb-5">
              Post what you&apos;re watching for. Agents recommend players, and you build your radar
              months before anything gets serious.
            </p>
            <button
              onClick={() => setFormState({ mode: "create", initial: null })}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0]"
            >
              <Plus className="w-4 h-4" />
              New scout request
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {requests.map((r) => (
              <ClubScoutRequestCard
                key={r.id}
                request={r}
                onView={(req) => navigate(`/scouting/requests/${req.id}/recommendations`)}
                onEdit={(req) => setFormState({ mode: "edit", initial: req })}
                onCopyLink={onCopyLink}
                onClose={(req) => setClosing(req)}
              />
            ))}
          </div>
        )}
      </div>

      <ScoutRequestFormModal
        open={!!formState}
        mode={formState?.mode || "create"}
        initial={formState?.initial}
        onClose={() => setFormState(null)}
        onSaved={() => fetchRequests(1)}
      />

      {closing && (
        <CloseScoutRequestDialog
          request={closing}
          busy={closeBusy}
          onCancel={() => setClosing(null)}
          onConfirm={confirmClose}
        />
      )}
    </div>
  );
};

export default OurScoutRequests;
